import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { trackEvent } from 'analytics/trackFunctions'

import { getCashoutLimit, getConcepts } from 'api/cashout'
import { useNewTransferStore } from 'store'
import { LIMIT_ERRORS } from 'types/api/transfer'

import PageWrapper from 'components/PageWrapper'
import { ProgressBar, AmountInput } from 'components/AdvanceOffer'
import CustomListbox from 'components/listbox'
import PrimaryButton from 'components/primaryButton'
import TextInputField from 'components/input'

const { ACCOUNT_WITHOUT_BALANCE } = LIMIT_ERRORS

export interface TransformedConceptItem {
  key: string
  name: string
}

const NewTransfer = () => {
  const [conceptList, setConceptList] = useState([])
  const [limit, setLimit] = useState<number | string>(0)
  const amount = useNewTransferStore((state) => state.amount)
  const concept = useNewTransferStore((state) => state.concept)
  const detail = useNewTransferStore((state) => state.detail)

  const { updateAmount, updateConcept, updateDetail } = useNewTransferStore()

  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const transferIsDisabled =
    !amount ||
    !detail ||
    !concept ||
    amount == '0' ||
    Number(amount) > Number(limit)

  useEffect(() => {
    trackEvent({ eventName: 'bille_pantalla_nueva_transferencia' })
    Promise.all([getConcepts(), getCashoutLimit()])
      .then((res) => {
        const [concepts, limits] = res
        if (concepts.result) {
          const transformedRes = Object.entries(concepts.result).map(
            ([key, value]) => ({
              key,
              name: value
            })
          )
          setConceptList(transformedRes)
          const defaultConcept = transformedRes.find(
            (c) => c.key === 'VAR'
          ) as TransformedConceptItem
          updateConcept(defaultConcept)
        }

        if (limits.success) {
          const { cashOutLimit } = limits.data.result
          setLimit(cashOutLimit)
        } else {
          const { code } = limits.error

          if (code === ACCOUNT_WITHOUT_BALANCE) {
            return setLimit('0')
          }
        }
      })
      // TO-DO: enviar a página de error
      .catch((error) => console.warn(error))
  }, [])

  return (
    <PageWrapper noTabBar title='Ingresar importe' viewSelected='/movimientos'>
      <section className='w-full flex flex-col mt-4 text-black items-center mx-auto sm:max-w-[25rem] h-[calc(100vh-12rem)]'>
        <AmountInput
          divider={1}
          minAmount={0}
          maxAmount={Number(limit)}
          ref={inputRef}
          value={amount}
          setValue={updateAmount}
          isCashout
        />
        <ProgressBar
          minValue={0}
          divider={1}
          maxValue={Number(limit)}
          setValue={updateAmount}
          value={Number(amount)}
          className='mt-3'
          inputRef={inputRef}
        />
        <p className='text-left self-start mt-3 text-xs font-medium'>
          Seleccioná un motivo
        </p>
        {conceptList ? (
          <div className='w-full'>
            <CustomListbox
              disabled={conceptList.length === 0}
              className='mt-2 !w-full'
              list={conceptList}
              selectedValue={concept}
              setSelectedValue={updateConcept}
              id='reason'
            />
          </div>
        ) : (
          <div className='w-full'>
            <Skeleton height={56} className='my-2' />
          </div>
        )}
        <p className='text-left self-start mt-3 text-xs font-medium'>
          Describí un detalle de la transferencia
        </p>
        <TextInputField
          value={detail}
          id='detail'
          inputType='textarea'
          margins='mt-2'
          maxLength={20}
          callbackFunction={({ target }) => {
            updateDetail(target.value)
          }}
        />
        <div className='justify-self-end flex-grow w-full items-end justify-end flex-col mt-2 flex md:mt-6 mb-8'>
          <PrimaryButton
            isDisabled={transferIsDisabled}
            buttonText='Continuar'
            primaryButtonFunction={() => {
              trackEvent({
                eventName: 'bille_nueva_transferencia_boton_transferir'
              })
              navigate('/transferencias/confirmar-transferencia', {
                replace: true
              })
            }}
          />
          <PrimaryButton
            outlined
            className='mt-4'
            buttonText='Cancelar'
            primaryButtonFunction={() =>
              navigate('/transferencias', { replace: true })
            }
          />
        </div>
      </section>
    </PageWrapper>
  )
}

export default NewTransfer
